import { Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import MissingKintaiDates from "../components/MissingKintaiDates";
import {
  savePendingKintai,
  cleanupPendingKintai,
  getPendingDates,
} from "../utils/pendingKintaiStorage";

// クローズしてからn日以内のプロジェクトのみ表示する
const CLOSED_WITHIN_DAYS = 60;

const Create = () => {
  const today = dayjs();
  const { currentUser, isContractor, refreshContractorStatus } = useAuth();
  const encodedEmail = encodeURIComponent(currentUser.email);

  const [beginHourOfWork, setBeginHourOfWork] = useState(0);
  const [beginMinuteOfWork, setBeginMinuteOfWork] = useState(0);

  const [fields, setFields] = useState([
    {
      id: 1,
      project: "",
      projectDetail: "",
      toHour: 0,
      toMinute: 0,
      prevId: null, // 初めの行はnull、それ以外は前の行のidを参照
    },
  ]);
  const [selectedDate, setSelectedDate] = useState(today); // 日付の状態を管理
  const [isOffice, setIsOffice] = useState(false); // 出社したかを示すチェックボックスの状態を管理
  const [nextId, setNextId] = useState(2);
  const [options, setOptions] = useState([]);
  const [projectDetails, setProjectDetails] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbarの状態を管理
  const [loading, setLoading] = useState(false); // ローディング状態を管理
  const [contracts, setContracts] = useState([]); // 契約データのキャッシュ
  const [outOfContractDialog, setOutOfContractDialog] = useState({
    open: false,
    outOfContractProjects: [],
  });
  const [hideClosedProjects, setHideClosedProjects] = useState(false); // "クローズ/デリバリ済み"を非表示のチェックボックスの状態を管理
  const [isShowAllProjects, setIsShowAllProjects] = useState(false); // "クローズ/デリバリ済み"含めて全プロジェクトを表示するかの状態を管理
  const [allProjects, setAllProjects] = useState([]); // 全プロジェクトの状態を管理
  const [isDayoffDate, setIsDayoffDate] = useState(false); // 選択した日付が休暇かの状態を管理
  const [missingKintaiDates, setMissingKintaiDates] = useState(null); // 勤怠未登録のステータスの状態を管理
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true); // サイドバーの開閉状態を管理
  /**
   * "date": status.date.isoformat(),
    "is_registered": status.is_registered
    "is_dayoff": status.is_dayoff
   */

  const fetchRecentProjects = useCallback(async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/recent_projects`,
      );
      if (response.status === 200) {
        return response.data || [];
      } else {
        console.error("直近のプロジェクトIDの取得に失敗しました");
        return [];
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
      return [];
    }
  }, []);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/contracts`,
      );
      if (response.status === 200) {
        setContracts(response.data);
      } else {
        console.error("契約データの取得に失敗しました");
      }
    } catch (error) {
      console.error("契約データ取得エラー:", error);
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/users/${encodedEmail}`,
      );
      if (response.status === 200) {
        const data = await response.data;
        const [defaultStartHour, defaultStartMinute] = (
          data.default_start_time || "09:30"
        ).split(":");
        setBeginHourOfWork(parseInt(defaultStartHour, 10));
        setBeginMinuteOfWork(parseInt(defaultStartMinute, 10));
        setFields([
          {
            id: 1,
            project: "",
            projectDetail: "",
            toHour: parseInt(defaultStartHour, 10),
            toMinute: parseInt(defaultStartMinute, 10),
            prevId: null,
          },
        ]);

        setFields([
          {
            id: 1,
            project: "",
            projectDetail: "",
            toHour: parseInt(defaultStartHour, 10),
            toMinute: parseInt(defaultStartMinute, 10),
            prevId: null,
          },
        ]);
        setNextId(2);
        setIsOffice(false);
      } else {
        console.error("ユーザ情報の取得に失敗しました");
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  }, [encodedEmail]);

  const fetchKintai = useCallback(async (date, details) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/kintais/${encodedEmail}/${date}`,
      );
      if (response.status === 200) {
        const data = await response.data;
        if (Object.keys(data).length === 0) {
          await fetchUserInfo();
          return;
        }
        const kintaiList = JSON.parse(data.kintais);
        console.log("details type:", typeof details, "isArray:", Array.isArray(details), details);
        const kintaiFields = kintaiList.map((kintai, index) => {
          const _details = Array.isArray(details) ? details : [];
          const relatedProjectDetails = _details.filter(
            (detail) => detail.project_no === String(kintai.project_no),
          );
          const isExistRelatedProject = relatedProjectDetails.some(
            (detail) => detail.id === kintai.project_detail_id,
          );
          return {
            id: index + 1,
            project: kintai.project_no,
            projectDetail: isExistRelatedProject
              ? kintai.project_detail_id
              : "",
            toHour: parseInt(kintai.to_time.split(":")[0], 10),
            toMinute: parseInt(kintai.to_time.split(":")[1], 10),
            prevId: index === 0 ? null : index,
          };
        });
        setBeginHourOfWork(parseInt(kintaiList[0].from_time.split(":")[0], 10));
        setBeginMinuteOfWork(
          parseInt(kintaiList[0].from_time.split(":")[1], 10),
        );
        setFields(kintaiFields);
        setNextId(kintaiFields.length + 1);
        setIsOffice(data.is_office);
      } else {
        console.error("勤怠情報の取得に失敗しました");
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  }, [encodedEmail, fetchUserInfo]);

  const fetchOptions = useCallback(async () => {
    try {
      const recentProjectIds = await fetchRecentProjects();
      // 60日以内にクローズされたプロジェクトも取得（明示的な根拠は無い仮ぎめ）
      const closed_within_days = isShowAllProjects ? 0 : 60;
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/projects`,
        {
          params: {
            include_closed: true,
            closed_within_days: closed_within_days,
          },
        },
      );
      if (response.status === 200) {
        const data = await response.data;
        let recentProjects = recentProjectIds
          .map((id) => data.find((project) => project.id === id))
          .filter(Boolean);
        recentProjects = recentProjects.sort((a, b) => a.id - b.id);
        const remainingProjects = data.filter(
          (project) => !recentProjectIds.includes(project.id),
        );
        const allProjects = [...recentProjects, ...remainingProjects];
        setAllProjects(allProjects);
        setOptions(
          hideClosedProjects
            ? allProjects.filter((project) => !project.is_closed)
            : allProjects,
        );
      } else {
        console.error("データの取得に失敗しました");
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  }, [hideClosedProjects, isShowAllProjects, fetchRecentProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchOptions();
      setLoading(false);
    })();
  }, [isShowAllProjects, fetchOptions]);

  const fetchMissingKintaiDates = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/kintai-status/current`,
      );
      if (response.status !== 200) {
        console.error("勤怠ステータスの取得に失敗しました");
      } else {
        const serverData = response.data;

        // ローカルストレージのクリーンアップ（登録済みデータの削除、期限切れ検出）
        const { expiredDates } = cleanupPendingKintai(currentUser.email, serverData);

        // 期限切れデータがあればエラーメッセージを表示
        if (expiredDates.length > 0) {
          const dateList = expiredDates.join(", ");
          alert(`以下の日付の勤怠登録に失敗した可能性があります。再登録してください。\n${dateList}`);
        }

        // 保留中のデータを`is_pending`フラグとして追加
        const pendingDates = getPendingDates(currentUser.email);
        const dataWithPending = serverData.map((status) => ({
          ...status,
          is_pending: pendingDates.includes(status.date),
        }));

        setMissingKintaiDates(dataWithPending);
        return dataWithPending;
      }
    } catch (error) {
      console.error("Error fetching Kintai status:", error);
      throw error; // Re-throw the error for further handling if needed
    }
  };

  useEffect(() => {
    /**案件情報は初期表示時のみ取得する */
    const fetchProjectDetails = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/projectdetails`,
        );
        if (response.status === 200) {
          const data = await response.data;
          setProjectDetails(data);
          return data;
        } else {
          console.error("データの取得に失敗しました");
        }
      } catch (error) {
        console.error("エラーが発生しました:", error);
      }
    };
    const fetchData = async () => {
      setLoading(true);
      // projectDtailsがStateにセットされる前にfetchKintaiが実行されるため、引数として渡す

      await Promise.all([
        fetchOptions(),
        fetchProjectDetails(),
        fetchMissingKintaiDates(),
        fetchContracts(),
      ]);
      setLoading(false);
    };

    fetchData();
    setFields([
      {
        id: 1,
        project: "",
        projectDetail: "",
        toHour: 0,
        toMinute: 0,
        prevId: null,
      },
    ]);
    setBeginHourOfWork(0);
    setBeginMinuteOfWork(0);
    setIsOffice(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    /** 入力対象日を変えた時は登録している勤怠情報を取得し、休暇状態を更新する */
    (async () => {
      if (projectDetails.length > 0) {
        setLoading(true);
        await fetchKintai(selectedDate.format("YYYY-MM-DD"), projectDetails); // projectとprojectDetailsのfetchが完了した後にkintaiをfetch
        setLoading(false);
      }
    })();
    setIsDayoffDate(
      missingKintaiDates?.some(
        (status) => status.date === selectedDate.format("YYYY-MM-DD") && status.is_dayoff,
      ),
    );
  }, [selectedDate, projectDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOptions(
      hideClosedProjects
        ? allProjects.filter((project) => !project.is_closed)
        : allProjects,
    );
  }, [hideClosedProjects, allProjects]);

  const handleAddRow = () => {
    const lastField = fields[fields.length - 1];
    const newField = {
      id: nextId,
      project: "",
      projectDetail: "",
      toHour: lastField.toHour,
      toMinute: lastField.toMinute,
      prevId: lastField.id,
    };
    setFields([...fields, newField]);
    setNextId(nextId + 1);
  };

  const handleDeleteRow = (id) => {
    const nextField = fields.find((field) => field.prevId === id);
    const deleteField = fields.find((field) => field.id === id);
    if (deleteField === undefined) {
      return; // 削除対処が見つからないので何もしない
    } else if (nextField === undefined) {
      // 削除対象が最後の行の場合は、その行を削除
      setFields(fields.filter((field) => field.id !== id));
    } else if (deleteField.prevId === null) {
      // 削除対象が最初の行の場合は、次の行を最初の行にする
      setBeginHourOfWork(nextField.toHour);
      setBeginMinuteOfWork(nextField.toMinute);
      setFields([{ ...fields[1], prevId: null }, ...fields.slice(2)]);
    } else {
      // 削除対象が中間の行の場合は、削除対象の前後を繋げる
      setFields(
        fields.reduce((acc, field) => {
          if (field.id === nextField.id) {
            return [...acc, { ...field, prevId: field.prevId }];
          } else if (field.id === id) {
            return acc;
          } else {
            return [...acc, field];
          }
        }, []),
      );
    }
  };

  const handleInputChange = (id, event, newValue, name) => {
    const newFields = fields.map((field) => {
      if (field.id === id) {
        return { ...field, [name]: newValue };
      }
      return field;
    });
    setFields(newFields);
  };

  const handleAutocompleteChange = (id, event, newValue, name) => {
    // const newFields = fields.map(field => {
    //   if (field.id === id) {
    //     return { ...field, [name]: newValue ? newValue.id : '' };
    //   }
    //   return field;
    // });
    const newFields = fields.map((field) => {
      if (field.id === id) {
        const updatedField = { ...field, [name]: newValue ? newValue.id : "" };
        if (name === "project" && newValue) {
          const relatedDetails = projectDetails.filter(
            (detail) => detail.project_no === String(newValue.id),
          );
          if (relatedDetails.length > 0) {
            updatedField.projectDetail = relatedDetails[0].id;
          } else {
            updatedField.projectDetail = "";
          }
        }
        return updatedField;
      }
      return field;
    });
    setFields(newFields);
  };

  const handleTimeInputChange = (id, event, newValue, name, options) => {
    const closestValue = options.reduce((prev, curr) =>
      Math.abs(curr - newValue) < Math.abs(prev - newValue) ? curr : prev
    );
    const newFields = fields.map((field) => {
      if (field.id === id) {
        return { ...field, [name]: closestValue };
      }
      return field;
    });
    setFields(newFields);
  };

  const [snackbarMessage, setSnackbarMessage] = useState("勤怠情報が保存されました");

  /**
   * 指定されたプロジェクトが契約期間外かどうかを判定
   */
  const isOutOfContract = (projectNo, targetDate) => {
    if (!projectNo || contracts.length === 0) {
      return false; // 安全側に倒す
    }

    const targetDateStr = targetDate.format("YYYY-MM-DD");

    // 該当プロジェクトの全契約を取得
    const projectContracts = contracts.filter(
      (c) => String(c.project_no) === String(projectNo),
    );

    if (projectContracts.length === 0) {
      return false; // 契約が存在しない場合は期間内とみなす
    }

    // いずれかの契約が期間内であればfalse（期間内）
    const isWithinAnyContract = projectContracts.some((contract) => {
      const { start_date, end_date } = contract;

      // start_date以降かチェック
      if (targetDateStr < start_date) return false;

      // end_dateがnullなら無期限契約（期間内）
      if (end_date === null) return true;

      // end_date以内かチェック
      return targetDateStr <= end_date;
    });

    return !isWithinAnyContract; // すべての契約が期間外の場合true
  };

  /**
   * 契約期間外判定マップをメモ化
   * レンダリング時のみ計算し、input時などには再計算しない
   */
  const outOfContractMap = useMemo(() => {
    const map = {};
    options.forEach((option) => {
      map[option.id] = isOutOfContract(option.id, selectedDate);
    });
    return map;
  }, [selectedDate, contracts, options]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (fields.some((field) => field.project === "")) {
      alert("業務内容は必須です");
      return;
    }

    const kintais = fields.map((field, idx) => ({
      project_no: field.project,
      project_detail_id: field.projectDetail,
      project_name: options.find((option) => option.id === field.project)?.name || "",
      // project_detail の id は一意でないので、業務もそろえて引く。
      project_detail_name: projectDetails.find(
        (detail) => detail.project_no === String(field.project) && detail.id === field.projectDetail,
      )?.name || "",
      from_time: idx === 0
        ? `${beginHourOfWork}:${beginMinuteOfWork}`
        : `${fields[idx - 1].toHour}:${fields[idx - 1].toMinute}`,
      to_time: `${field.toHour}:${field.toMinute}`,
    }));

    for (let i = 0; i < kintais.length; i++) {
      const fromParts = kintais[i].from_time.split(":");
      const toParts = kintais[i].to_time.split(":");
      const fromMinutes = parseInt(fromParts[0]) * 60 + parseInt(fromParts[1]);
      const toMinutes = parseInt(toParts[0]) * 60 + parseInt(toParts[1]);

      if (toMinutes <= fromMinutes) {
        alert(`業務${i + 1}: 終了時間は開始時間より後である必要があります`);
        return;
      }
    }

    // 契約期間外チェック
    const outOfContractProjects = fields
      .filter((field) => outOfContractMap[field.project] || false)
      .map((field) => options.find((opt) => opt.id === field.project)?.name)
      .filter((name) => name);

    if (outOfContractProjects.length > 0) {
      setOutOfContractDialog({
        open: true,
        outOfContractProjects: outOfContractProjects,
      });
      setLoading(false);
      return;
    }

    // 実際の登録処理を実行
    await executeSubmit();
  };

  /**
   * 実際の登録処理（ダイアログからも呼び出し可能）
   */
  const executeSubmit = async () => {
    setLoading(true);
    const date = selectedDate.format("YYYY-MM-DD");
    const kintais = fields.map((field, idx) => ({
      project_no: field.project,
      project_detail_id: field.projectDetail,
      project_name: options.find((option) => option.id === field.project)?.name || "",
      // project_detail の id は一意でないので、業務もそろえて引く。
      project_detail_name: projectDetails.find(
        (detail) => detail.project_no === String(field.project) && detail.id === field.projectDetail,
      )?.name || "",
      from_time: idx === 0
        ? `${beginHourOfWork}:${beginMinuteOfWork}`
        : `${fields[idx - 1].toHour}:${fields[idx - 1].toMinute}`,
      to_time: `${field.toHour}:${field.toMinute}`,
    }));

    const user_id = encodedEmail;
    const payload = {
      user_id,
      date,
      kintais,
      is_office: isOffice,
    };

    // 楽観的UI: 即座にフィードバックを表示
    setSnackbarMessage("勤怠情報を保存中...");
    setSnackbarOpen(true);

    // 楽観的UI: missingKintaiDatesを「システム反映中」として更新（消さない）
    const previousMissingKintaiDates = missingKintaiDates;
    if (missingKintaiDates) {
      setMissingKintaiDates(
        missingKintaiDates.map((status) =>
          status.date === date ? { ...status, is_pending: true } : status
        )
      );
    }

    try {
      const endpoint = isContractor ? "/contractors/kintais/" : "/kintais/";
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        payload,
        { timeout: 300000 } // タイムアウトを5分に設定
      );

      if (response.status === 200) {
        console.log("データが正常に送信されました");

        // ローカルストレージに保留中データとして保存
        savePendingKintai(currentUser.email, date, kintais, isOffice);

        setSnackbarMessage("勤怠情報が登録されました。システムに反映されるまで数十秒かかる可能性があります");
      } else {
        console.error("データ送信に失敗しました");
        setSnackbarMessage("データ送信に失敗しました");
        // ロールバック
        setMissingKintaiDates(previousMissingKintaiDates);
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
      if (error.response && error.response.status === 403) {
        // 社員用と業務委託者用のエンドポイントを取り違えた（区分が切り替わった）ときの 403。
        // 区分は freee_id の有無で決まり、サーバー側の判定は最大5分前の値が残ることがある（issue #85）。
        await refreshContractorStatus();
        setSnackbarMessage(
          "社員／業務委託の区分が切り替わった可能性があります。数分待ってから、もう一度登録してください",
        );
      } else if (error.response?.status === 400 && typeof error.response.data?.detail === "string") {
        // 入力内容の誤り（詳細業務がその業務のものでない等）。理由をそのまま出す。
        setSnackbarMessage(error.response.data.detail);
      } else {
        setSnackbarMessage("エラーが発生しました");
      }
      // ロールバック
      setMissingKintaiDates(previousMissingKintaiDates);
    }
    setLoading(false);
  };

  /**
   * 契約期間外確認ダイアログの確定処理
   */
  const handleOutOfContractConfirm = () => {
    setOutOfContractDialog({ open: false, outOfContractProjects: [] });
    executeSubmit();
  };

  /**
   * 契約期間外確認ダイアログのキャンセル処理
   */
  const handleOutOfContractCancel = () => {
    setOutOfContractDialog({ open: false, outOfContractProjects: [] });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // 日付を跨いだ勤務を登録できるように最大29時までとする。
  const hours = Array.from({ length: 30 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <>
      <Container maxWidth="xl">
        <Box display="flex" gap={isSidebarExpanded ? 3 : 1}>
          <Box flex={0} sx={{ transition: "all 0.3s ease-in-out" }}>
            <MissingKintaiDates
              encodedEmail={encodedEmail}
              missingKintaiDates={missingKintaiDates}
              setMissingKintaiDates={setMissingKintaiDates}
              setSelectedDate={setSelectedDate}
              onExpandChange={setIsSidebarExpanded}
            />
          </Box>
          <Box flex={1}>
            <Box
              display="flex"
              mt={4}
              mb={4}
              sx={{ "& .MuiTextField-root": { width: "20ch" } }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="日付"
                  value={selectedDate}
                  defaultValue={today}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  sx={{ width: 150, height: 40 }}
                />
              </LocalizationProvider>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isOffice}
                    onChange={(e) => setIsOffice(e.target.checked)}
                    // color="primary"
                  />
                }
                label="大手町出社"
                sx={{ ml: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideClosedProjects}
                    onChange={(e) => setHideClosedProjects(e.target.checked)}
                  />
                }
                label='"クローズ/デリバリ済み"を非表示'
                sx={{ ml: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isShowAllProjects}
                    onChange={(e) => setIsShowAllProjects(e.target.checked)}
                  />
                }
                label={
                  // 上揃えにする


                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <p style={{ margin: 0 }}>過去全ての案件を表示</p>
                      <p style={{ margin: 0, fontSize: "0.8em" }}>
                        {isShowAllProjects
                          ? "※全ての期間の案件を表示中"
                          : `※クローズ/デリバリして${CLOSED_WITHIN_DAYS}日以内の案件のみ表示中`}
                      </p>
                    </div>

                }
                sx={{ ml: 2 }}
              />
            </Box>
            <Typography color="red">
              {isDayoffDate ? "休暇登録済み" : ""}
            </Typography>
            {fields.map((field, idx) => {
              const relatedProjectDetails = projectDetails.filter(
                (detail) => detail.project_no === field.project.toString(),
              );
              return (
                <Box
                  key={field.id}
                  display="flex"
                  alignItems="center"
                  mb={2}
                  sx={{
                    "& .MuiTextField-root": { mr: 1 },
                  }}
                >
                  <Autocomplete
                    options={options} // 本来はAPIから取得する
                    getOptionLabel={(option) => option.name}
                    value={options.find((option) => option.id === field.project)
                      || null}
                    onChange={(event, newValue) =>
                      handleAutocompleteChange(
                        field.id,
                        event,
                        newValue,
                        "project",
                      )}
                    onBlur={(event) => {
                      const inputValue = event.target.value;
                      const filteredOptions = options.filter((option) => option.name.includes(inputValue));
                      if (filteredOptions.length === 1) {
                        handleAutocompleteChange(
                          field.id,
                          event,
                          filteredOptions[0],
                          "project",
                        );
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderOption={(props, option) => {
                      const outOfContract = outOfContractMap[option.id] || false;
                      return (
                        <li {...props} style={{ color: outOfContract ? "red" : "inherit" }}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const outOfContract = outOfContractMap[field.project] || false;

                      return (
                        <TextField
                          {...params}
                          label="業務"
                          size="small"
                          sx={{
                            width: 300,
                            "& .MuiInputBase-input": {
                              color: outOfContract ? "red" : "inherit",
                            },
                            "& .MuiAutocomplete-input": {
                              color: outOfContract ? "red" : "inherit",
                            },
                          }}
                          name="project"
                          multiline
                        />
                      );
                    }}
                  />
                  <Autocomplete
                    options={relatedProjectDetails}
                    getOptionLabel={(option) => option.name}
                    value={relatedProjectDetails.find(
                      (option) => option.id === field.projectDetail,
                    ) || null}
                    onChange={(event, newValue) =>
                      handleAutocompleteChange(
                        field.id,
                        event,
                        newValue,
                        "projectDetail",
                      )}
                    onBlur={(event) => {
                      const inputValue = event.target.value;
                      const filteredOptions = relatedProjectDetails.filter((option) => option.name.includes(inputValue));
                      // 空は「詳細なし」として残す。空文字は全候補に一致し、候補1件の業務で勝手に入るため。
                      if (inputValue && filteredOptions.length === 1) {
                        handleAutocompleteChange(
                          field.id,
                          event,
                          filteredOptions[0],
                          "projectDetail",
                        );
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="詳細業務"
                        size="small"
                        sx={{ width: 200 }}
                        name="projectDetail"
                      />
                    )}
                  />
                  <Autocomplete
                    options={hours}
                    getOptionLabel={(option) => option.toString()}
                    value={idx === 0 ? beginHourOfWork : fields[idx - 1].toHour}
                    onChange={(event, newValue) =>
                      idx === 0
                        ? setBeginHourOfWork(newValue)
                        : handleInputChange(
                          field.prevId,
                          event,
                          newValue,
                          "toHour",
                        )}
                    onBlur={(event) =>
                      idx === 0
                        ? setBeginHourOfWork(parseInt(event.target.value))
                        : handleTimeInputChange(
                          field.prevId,
                          event,
                          parseInt(event.target.value),
                          "toHour",
                          hours,
                        )}
                    defaultValue={0}
                    disableClearable
                    sx={{ mr: 0.1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="時"
                        variant="outlined"
                        size="small"
                        sx={{ width: 70 }}
                        name="fromHour"
                      />
                    )}
                  />
                  <Autocomplete
                    options={minutes}
                    getOptionLabel={(option) => option.toString()}
                    value={idx === 0 ? beginMinuteOfWork : fields[idx - 1].toMinute}
                    onChange={(event, newValue) =>
                      idx === 0
                        ? setBeginMinuteOfWork(newValue)
                        : handleInputChange(
                          field.prevId,
                          event,
                          newValue,
                          "toMinute",
                        )}
                    onBlur={(event) =>
                      idx === 0
                        ? setBeginMinuteOfWork(parseInt(event.target.value))
                        : handleTimeInputChange(
                          field.prevId,
                          event,
                          parseInt(event.target.value),
                          "toMinute",
                          minutes,
                        )}
                    disableClearable
                    sx={{ mr: 0.1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="分"
                        variant="outlined"
                        size="small"
                        sx={{ width: 70 }}
                        name="fromMinute"
                      />
                    )}
                  />
                  <Box component="label" sx={{ mr: 1 }}>~</Box>
                  <Autocomplete
                    options={hours}
                    getOptionLabel={(option) => option.toString()}
                    value={field.toHour}
                    onChange={(event, newValue) => handleInputChange(field.id, event, newValue, "toHour")}
                    onBlur={(event) =>
                      handleTimeInputChange(
                        field.id,
                        event,
                        parseInt(event.target.value),
                        "toHour",
                        hours,
                      )}
                    disableClearable
                    sx={{ mr: 0.1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="時"
                        variant="outlined"
                        size="small"
                        sx={{ width: 70 }}
                        name="toHour"
                      />
                    )}
                  />
                  <Autocomplete
                    options={minutes}
                    getOptionLabel={(option) => option.toString()}
                    value={field.toMinute}
                    onChange={(event, newValue) => handleInputChange(field.id, event, newValue, "toMinute")}
                    onBlur={(event) =>
                      handleTimeInputChange(
                        field.id,
                        event,
                        parseInt(event.target.value),
                        "toMinute",
                        minutes,
                      )}
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="分"
                        variant="outlined"
                        size="small"
                        sx={{ width: 70 }}
                        name="toMinute"
                      />
                    )}
                  />
                  <Box>
                    {idx === fields.length - 1 && (
                      <Button color="primary" onClick={handleAddRow}>
                        <AddIcon></AddIcon>
                      </Button>
                    )}
                    {fields.length > 1 && (
                      <Button
                        color="primary"
                        onClick={() => handleDeleteRow(field.id)}
                      >
                        <Delete></Delete>
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Box display="flex" justifyContent="space-between" mt={4} mb={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                登録
              </Button>
              <Dialog open={loading}>
                <DialogContent>
                  <CircularProgress />
                </DialogContent>
              </Dialog>
              {/* 契約期間外確認ダイアログ */}
              <Dialog
                open={outOfContractDialog.open}
                onClose={handleOutOfContractCancel}
              >
                <DialogTitle>契約期間外の業務が含まれています</DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{ whiteSpace: "pre-line" }}>
                    以下の業務は契約期間外です。本当に登録しますか？
                    {"\n\n"}
                    {outOfContractDialog.outOfContractProjects.map((name, idx) => (
                      <span key={idx}>
                        ・{name}
                        {"\n"}
                      </span>
                    ))}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleOutOfContractCancel} color="primary">
                    キャンセル
                  </Button>
                  <Button onClick={handleOutOfContractConfirm} color="primary" autoFocus>
                    登録する
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={5000}
              onClose={handleSnackbarClose}
              message={snackbarMessage}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />
          </Box>
        </Box>
      </Container>
    </>
  );
};
export default Create;
